import { Component, OnDestroy, OnInit, Renderer2, effect } from '@angular/core';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { OptInModalComponent } from '../../../shared/components/opt-in/opt-in-modal.component';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Subject, zip } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  publishReplay,
  refCount,
  skipUntil,
  take,
  takeUntil,
} from 'rxjs/operators';
import {
  displayMobileCard,
  selectAccounts,
  selectAccountsLoaded,
  selectHasAccountPastDue,
  selectIsCardOnlyUser,
} from 'src/app/feature-modules/accounts/store/accounts.selector';
import { selectCreditHealthLoaded } from 'src/app/feature-modules/credit-health/store/credit-health.selector';
import {
  selectCustomerAttributesLoaded,
  selectTrustedContactFirstName,
} from 'src/app/feature-modules/customer-attributes/store/customer-attributes.selector';
import {
  selectHasHslHubOffer,
  selectHubOffers,
} from 'src/app/feature-modules/offers/store/selectors/offers.selectors';

import { AuthEventRepo, AuthEventType } from 'src/app/enums/event/event.enum';
import { ExperimentSeen } from 'src/app/models/event-data.model';
import { EventTrackingService } from 'src/app/services/event-tracking.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { OptInService } from 'src/app/services/opt-in.service';
import { getHubFeatureFlagActions } from 'src/app/store/actions/feature-flag.action';
import {
  selectHubFeatureFlags,
  selectHubFeatureFlagsProcessed,
  selectHubIsDebtConsolidationEnabled,
  selectHubIsRefiFirstEnabled,
} from 'src/app/store/selectors/feature-flag.selector';
import { getFeatureFlagEnabledByName$ } from 'src/app/utils/feature-flag.util';
import { SegmentJsAnalyticsService } from '../../../../services/segment-js-analytics.service';
import { Offer } from '../../../offers/models/offers.model';
import { dismissHubOffer } from '../../../offers/store/actions/offers.actions';
import { selectOptIn } from '../../../opt-in/store/opt-in.selectors';
import { selectProfilePaperless } from '../../../profile/store/selectors/profile.selectors';
import { SunsetModalComponent } from '../../components/sunset-modal/sunset-modal.component';
import { SlideOutService } from '../../services/slide-out.service';

const getOptInModalConfig = (cid: string): MatDialogConfig<{ cid: string }> => ({
  maxWidth: '1120px',
  autoFocus: false,
  disableClose: true,
  panelClass: 'bewp-opt-in-modal',
  data: { cid },
});

@Component({
  selector: 'app-customer-hub-v2',
  templateUrl: './customer-hub-v2.component.html',
  styleUrls: ['./customer-hub-v2.component.scss'],
  standalone: false,
})
export class CustomerHubV2Component implements OnInit, OnDestroy {
  cid: string;
  hideOptInBanner: boolean = true;
  displayOptInModal: boolean;
  isMobile = window.innerWidth < 768 || false;
  onlyClosedAccounts = false;

  displayMobileCard = this.store.selectSignal(displayMobileCard);
  hasHslOffer = this.store.selectSignal(selectHasHslHubOffer);
  isHslSlideOutOpen = this.slideOutService.isHslSlideOutOpen;
  activeSlideOutOffer = this.slideOutService.activeOffer;


  //state selectors
  accounts$ = this.store.select(selectAccounts).pipe(publishReplay(1), refCount());
  paperlessProfiles$ = this.store.select(selectProfilePaperless);
  offers$ = this.store.select(selectHubOffers);
  hasPastDueAccount$ = this.store.select(selectHasAccountPastDue);

  //feature flags
  featureFlags$ = this.store.select(selectHubFeatureFlags);
  isOptInEnabled$ = this.featureFlags$.pipe(getFeatureFlagEnabledByName$('AWEE395ExpressLaneOptInToggle'));
  isOptInModalEnabled$ = this.featureFlags$.pipe(getFeatureFlagEnabledByName$('AWEE395ExpressLaneOptInModal'));
  isStickyOfferBannerEnabled$ = this.featureFlags$.pipe(getFeatureFlagEnabledByName$('AWEE491CrossSellStickyBanner'));
  isRefiFirstInCarouselEnabled = this.store.selectSignal(selectHubIsRefiFirstEnabled);
  isDebtConsolidationEnabled = this.store.selectSignal(selectHubIsDebtConsolidationEnabled);
  //opt in modal - (optInEnabled, modal enabled, no past due)
  displayOptInModal$ = zip(this.isOptInEnabled$, this.isOptInModalEnabled$, this.hasPastDueAccount$).pipe(
    map(([optInOn, optInModalOn, hasPasDueAcct]) => optInOn && optInModalOn && !hasPasDueAcct)
  );

  firstName$ = this.store.select(selectTrustedContactFirstName);

  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

  isHubLoaded$ = zip(
    this.store.select(selectCustomerAttributesLoaded),
    this.store.select(selectAccountsLoaded),
    this.store.select(selectCreditHealthLoaded),
    this.store.select(selectHubFeatureFlagsProcessed)
  ).pipe(
    map((loadedStates) => loadedStates.every((isLoaded) => isLoaded === true)),
    first((allLoaded) => allLoaded === true),
    takeUntil(this.ngUnsubscribe$)
  );

  constructor(
    private readonly store: Store,
    private readonly cookieService: CookieService,
    private readonly titleService: Title,
    private readonly eventTrackingService: EventTrackingService,
    public dialog: MatDialog,
    private readonly optInService: OptInService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly renderer: Renderer2,
    private readonly slideOutService: SlideOutService,
    private readonly segmentJsAnalyticsService: SegmentJsAnalyticsService
  ) {

  }

  onCloseOfferClick() {
    this.store.dispatch(dismissHubOffer());
  }

  ngOnInit() {
    this.cid = this.cookieService.get('cid');
    this.titleService.setTitle('Best Egg Customer Home');

    this.eventTrackingService.trackPageViewEvent({
      eventName: 'pageView',
      siteSection: 'hub',
    });

    this.segmentJsAnalyticsService.identify();

    this.store
      .select(selectIsCardOnlyUser)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isCardOnlyUser) => {
        this.toggleAdaDisplay(isCardOnlyUser);
      });

    combineLatest([
      this.accounts$,
      this.displayOptInModal$.pipe(distinctUntilChanged()),
      this.store.select(selectOptIn),
    ])
      .pipe(
        skipUntil(this.isHubLoaded$),
        filter(([accounts, displayModal, optIn]) => accounts?.loanAccounts?.length > 0 && !!optIn),
        first()
      )
      .subscribe(([accounts, displayModal, optIn]) => {
        const cardOptIn = optIn.find((obj) => obj.featureType === 'CC_MOBILE_APP_RETIREMENT_MODAL');
        if (accounts.cardAccounts.length > 0 && cardOptIn && !cardOptIn?.optedIn) {
          this.dialog
            .open(SunsetModalComponent)
            .afterClosed()
            .pipe(take(1))
            .subscribe((res) => {
              this.optInService
                .updateOptIn([
                  {
                    featureType: 'CC_MOBILE_APP_RETIREMENT_MODAL',
                    optedIn: true,
                  },
                ])
                .pipe(take(1))
                .subscribe();
            });
        } else if (displayModal) {
          this.displayOptInModal = true;
          setTimeout(() => {
            if (this.displayOptInModal) {
              this.openOptInModal();
              this.displayOptInModal = false;
            }
          }, 1000);
        }
      });
  }

  ngOnDestroy() {
    this.dialog.closeAll();
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  toggleAdaDisplay(isCardOnlyUser: boolean) {
    if (isCardOnlyUser) {
      this.renderer.addClass(document.body, 'hide-ada');
    } else {
      this.renderer.removeClass(document.body, 'hide-ada');
    }
  }

  openOptInModal() {
    if (!this.displayOptInModal) {
      return;
    }
    this.dialog.closeAll();

    this.dialog
      .open(OptInModalComponent, getOptInModalConfig(this.cid))
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res?.agreeToOptIn) {
          this.optInService
            .acceptOptIn()
            .pipe(take(1))
            .subscribe(
              () => {
                this.store.dispatch(getHubFeatureFlagActions.start({ request: 'hub' }));
              },
              (error) => console.error(error)
            );
          this.hideOptInBanner = true;
        } else {
          this.displayOptInModal = false;
          this.featureFlagService
            .updateFeatureFlag([
              {
                enabled: false,
                featureFlagName: 'AWEE395ExpressLaneOptInModal',
              },
            ])
            .pipe(take(1))
            .subscribe(
              () => {
                this.store.dispatch(getHubFeatureFlagActions.start({ request: 'hub' }));
              },
              (error) => console.error(error)
            );
        }
      });
  }

  trackClickMediaCardEvent($event) {
    this.eventTrackingService.trackEnterpriseEvent({
      eventName: 'viewKnowledgeBaseArticle',
      cid: this.cid,
      article: $event,
    });
  }

  openSlideOut(offer: Offer) {
    if (offer.subProductType === 'SECURED_HOME_LOAN') {
      this.slideOutService.open('hsl', offer, '_CAROUSEL');
    }
  }
}
